import { useEffect } from "react";
import PulseBg from "@assets/pulse/pulse-background.jpg";
import PulseBgMobile from "@assets/pulse/pulse-background-mobile.jpg";
import NoAdSettings from "@components/Advertisement/NoAdSettings";
import GAData from "@components/GAData/GAData";
import MetaTags from "@components/MetaTags/MetaTags";
import { useNavigateCarousel } from "@features/pulse/hooks/useNavigateCarousel";
import { useSetPulseArticle } from "@features/pulse/hooks/useSetPulseArticle";
import PulseLayout from "@features/pulse/layouts/PulseLayout";
import { getPulseSeoData } from "@features/pulse/utils/helpers";
import type { PulsePageContext } from "@features/pulse/utils/types";
import { useWindowSize } from "@hooks/useWindowSize";
import { dayjsSingaporeTimezone } from "@util/constant";
import { getPulseJSONLD } from "@util/helpers";
import dayjs from "dayjs";

const PulsePageContent = ({
  data: { pulseArticles },
}: PulsePageContext): React.ReactElement => {
  const { isScreenLG } = useWindowSize();

  const { sortedPulseArticles } = useSetPulseArticle({
    pulseArticles,
  });

  const imageUrl = isScreenLG ? PulseBg : PulseBgMobile;

  const {
    offset,
    activeArticle,
    handleNavigateLeftCard,
    handleNavigateRightCard,
    handleSetActiveArticle,
  } = useNavigateCarousel({
    articleList: sortedPulseArticles,
  });

  const firstArticle = pulseArticles?.[0];

  const {
    title: seoTitle,
    description,
    slug,
    imageUrl: imageUrlSeo,
  } = getPulseSeoData(firstArticle);

  const gaPubDate =
    activeArticle === sortedPulseArticles.length
      ? "(not set)"
      : dayjs(firstArticle?.published)
          .tz(dayjsSingaporeTimezone)
          .format("YYYY-MM-DD HH:mm:ss");

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.document.body.style.backgroundColor = "#0a0a0a";

    window.document.body.style.overflowY = "hidden";
  }, []);

  return (
    <div
      className="max-w-screen relative left-1/2 mx-auto flex h-svh w-screen -translate-x-1/2 flex-col overflow-hidden bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${imageUrl})`,
      }}
    >
      <NoAdSettings />

      <GAData
        event="virtual_pv"
        level2="pulse"
        contentcat={1}
        contenttype={activeArticle === sortedPulseArticles.length ? "" : "1"}
        title={seoTitle}
        articleid={firstArticle?.id || ""}
        pubdate={gaPubDate}
        author={firstArticle?.authors || "(not set)"}
        gsChannels="(not set)"
        pagination={activeArticle}
      />

      <MetaTags
        title={seoTitle}
        description={description}
        ogDescription={description}
        ogType="website"
        slug={slug}
        imageUrl={imageUrlSeo}
        imageAlt="Pulse"
        jsonLd={
          firstArticle
            ? getPulseJSONLD(firstArticle, description, slug)
            : undefined
        }
      />

      <PulseLayout
        sortedPulseArticles={sortedPulseArticles}
        activeArticle={activeArticle}
        handleNavigateRightCard={handleNavigateRightCard}
        handleNavigateLeftCard={handleNavigateLeftCard}
        handleSetActiveArticle={handleSetActiveArticle}
        offset={offset}
      />
    </div>
  );
};

export default PulsePageContent;
